import React from "react";
import { Card } from "./Card";
import { formatCurrency, displayWholeNumber } from "../utils/formatting";
import { cn } from "../utils/cn";
import type { ReadinessCopy } from "../utils/dashboardCopy";

interface MainResultCardProps {
  retirementAge: number;
  finalAmount: number;
  currency: string;
  /** Brief explanation displayed below the amount */
  subtitle?: string;
  /** Projected liquid assets at retirement (RRSP, TFSA, investments, cash) */
  liquidAssets?: number | null;
  /** Projected total liabilities at retirement */
  liabilities?: number | null;
  /** Readiness-based emotional copy for the hero card */
  readinessCopy?: ReadinessCopy;
  /** Handler when "See recommendations" link is clicked */
  onSeeRecommendations?: () => void;
  /** Whether the card shows scenario (what-if) mode values */
  isScenarioMode?: boolean;
  /** Handler for "View projection details" link inside the card */
  onViewDetails?: () => void;
}

/** Emoji prefix per readiness tier */
const TIER_EMOJI: Record<string, string> = {
  excellent: "✨",
  "on-track": "👍",
  "getting-there": "💪",
  "needs-attention": "⚠️",
};

export const MainResultCard = React.memo(
  ({
    retirementAge,
    finalAmount,
    currency,
    subtitle,
    liquidAssets,
    liabilities,
    readinessCopy,
    onSeeRecommendations,
    isScenarioMode = false,
    onViewDetails,
  }: MainResultCardProps) => {
    return (
      <Card
        gradient={!isScenarioMode}
        className={cn(
          "result-card-container",
          isScenarioMode && "result-card-container--scenario"
        )}
        id="main-result"
      >
        <div className="result-content">
          {/* Scenario mode chip */}
          {isScenarioMode && (
            <span className="scenario-mode-chip" id="scenario-mode-chip">
              ⚡ Scenario Mode
            </span>
          )}

          <p className="result-label">
            At age {displayWholeNumber(retirementAge)}, you'll have
          </p>
          <h2
            className={cn(
              "result-value",
              isScenarioMode && "result-value--scenario",
              finalAmount >= 10000000 && "result-value--large",
              finalAmount <= 0 && "result-value--empty"
            )}
          >
            {formatCurrency(finalAmount, currency)}
          </h2>
          {finalAmount <= 0 && (
            <p className="result-empty-hint">
              Enter your financial details to see your projection
            </p>
          )}
          {subtitle && <p className="result-subtitle">{subtitle}</p>}

          {/* Readiness-based emotional copy — inline, no colored block */}
          {readinessCopy && (
            <div className="readiness-copy" id="readiness-copy">
              <span className="readiness-copy-emoji">
                {TIER_EMOJI[readinessCopy.tier] ?? ""}
              </span>
              <div>
                <span
                  className={cn(
                    "readiness-copy-headline",
                    `readiness-copy-headline--${readinessCopy.tier}`
                  )}
                >
                  {readinessCopy.headline}
                </span>
                <span className="readiness-copy-separator"> — </span>
                <span className="readiness-copy-description">
                  {readinessCopy.description}
                  {readinessCopy.showRecommendationsLink &&
                    onSeeRecommendations && (
                      <>
                        {" "}
                        <button
                          type="button"
                          className="readiness-copy-link"
                          onClick={onSeeRecommendations}
                        >
                          See recommendations →
                        </button>
                      </>
                    )}
                </span>
              </div>
            </div>
          )}

          {/* Bridging label + sub-metrics */}
          {(liquidAssets != null || liabilities != null) && (
            <>
              <p className="result-breakdown-label">This breaks down as:</p>
              <div className="result-sub-metrics">
                {liquidAssets != null && (
                  <div className="liquid-assets-metric">
                    <p className="metric-label">Liquid Assets</p>
                    <p className="metric-value-positive-combined">
                      {formatCurrency(liquidAssets, currency)}
                    </p>
                  </div>
                )}
                {liabilities != null && (
                  <div className="liabilities-metric">
                    <p className="metric-label">Liabilities</p>
                    <p className="metric-value-negative-combined">
                      {liabilities > 0
                        ? formatCurrency(liabilities, currency)
                        : formatCurrency(0, currency)}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Footer with View Details link */}
          {onViewDetails && (
            <div className="result-card-footer">
              <button
                type="button"
                className="result-card-details-link"
                onClick={onViewDetails}
              >
                View projection details →
              </button>
            </div>
          )}
        </div>
      </Card>
    );
  }
);

MainResultCard.displayName = "MainResultCard";
